import { useState, useEffect } from 'react';
import { fmtDupr, fmtDuprRange, hasRange, rangeOverlap } from '../utils/skillLevels';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Check, Plus, X, Info, Zap } from 'lucide-react';
import { fmtDate } from '../utils/dates';
import { toast } from 'sonner';
import { sportsApi } from '../SportsAuthContext';
import SportsNavBar from '../components/SportsNavBar';
import { SportIcon } from '../components/SportIcon';
import CalendarPicker from '../components/CalendarPicker';
import PayOptionsPicker from '../components/PayOptionsPicker';
import { ymdTz } from '../utils/sportsUtils';

const TIMES = (() => {
  const t = [];
  for (let h = 0; h < 24; h++)
    for (let m = 0; m < 60; m += 30)
      t.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  return t;
})();

function displayTime(t) {
  if (!t) return '';
  const [hs, ms] = t.split(':');
  let h = parseInt(hs, 10);
  const m = parseInt(ms, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, '0')} ${ampm}`;
}

const STEPS = ['Basics', 'Format', 'Courts', 'Divisions', 'Registration', 'Check-In', 'Review'];

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 8, background: '#0A0A0A',
  border: '1px solid #2A2A2A', color: '#fff', fontSize: 14, outline: 'none',
};
const labelStyle = { fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#737373', marginBottom: 6, display: 'block' };

function OptionCard({ active, title, desc, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} type="button"
      className="text-left p-4 rounded-xl transition-all w-full"
      style={{
        background: active ? 'rgba(230,57,70,0.12)' : '#111',
        border: `1px solid ${active ? '#E63946' : '#2A2A2A'}`,
      }}>
      <span className="text-sm font-bold" style={{ color: '#fff' }}>{title}</span>
      {desc && <p className="text-xs mt-1" style={{ color: '#737373' }}>{desc}</p>}
    </button>
  );
}

function Tooltip({ text }) {
  const [show, setShow] = useState(false);
  return (
    <span className="relative inline-block ml-1">
      <Info className="h-3.5 w-3.5 cursor-help" style={{ color: '#555' }}
        onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)} />
      {show && (
        <span className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 rounded-lg text-xs whitespace-nowrap"
          style={{ background: '#1C1C1C', color: '#A3A3A3', border: '1px solid #333', maxWidth: 280, whiteSpace: 'normal' }}>
          {text}
        </span>
      )}
    </span>
  );
}

export default function SportsOpenPlayWizardPage() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [courts, setCourts] = useState([]);
  const today = ymdTz('Asia/Manila');

  const [form, setForm] = useState({
    name: '', description: '', date: '', time_start: '08:00', time_end: '18:00',
    game_mode: 'doubles', best_of: 1,
    scoring_target: 21, unlimited_scoring: false, win_by: 2,
    skill_based_pairing: false,
    gender_balance: true,
    balancing_factor: null,
    courts_used: [],
    divisions: [],
    entry_fee: '', registration_mode: 'open',
    payment_amount_options: ['full', 'deposit50', 'counter'],
    check_in_mode: 'both',
    rsvp_expire_hours_before: 24,
    scorer_pin: '',
    contact_name: '', contact_email: '', contact_phone: '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Division form
  const [divName, setDivName] = useState('');
  const [divCap, setDivCap] = useState('');
  const [divCourts, setDivCourts] = useState([]);

  useEffect(() => {
    sportsApi.get('/sports/courts').then(r => setCourts(r.data.filter(c => c.active !== false)));
    if (editId) {
      sportsApi.get(`/sports/open-play/${editId}`).then(r => {
        const s = r.data;
        setForm({
          name: s.name || '', description: s.description || '',
          date: s.date || '', time_start: s.time_start || '08:00', time_end: s.time_end || '18:00',
          game_mode: s.game_mode || 'doubles', best_of: s.best_of || 1,
          scoring_target: s.scoring_target || 21,
          unlimited_scoring: !s.scoring_target,
          win_by: s.win_by || 2,
          skill_based_pairing: s.skill_based_pairing || false,
          gender_balance: s.gender_balance !== false,
          balancing_factor: s.balancing_factor || null,
          courts_used: s.courts_used || [],
          divisions: s.divisions || [],
          entry_fee: s.entry_fee || '', registration_mode: s.registration_mode || 'open',
          payment_amount_options: s.payment_amount_options || ['full', 'deposit50', 'counter'],
          check_in_mode: s.check_in_mode || 'both',
          rsvp_expire_hours_before: s.rsvp_expire_hours_before ?? 24,
          scorer_pin: s.scorer_pin || '',
          contact_name: s.contact_name || '', contact_email: s.contact_email || '',
          contact_phone: s.contact_phone || '',
        });
      });
    }
  }, [editId]);

  // Match duration is no longer asked in the wizard: the backend seeds it from
  // Best Of (1→15, 3→25, 5→40 min) and the live system replaces it with the
  // rolling average of real completed matches. Used here only for capacity math.
  const BEST_OF_MINUTES = { 1: 15, 3: 25, 5: 40 };
  const estMatchMinutes = BEST_OF_MINUTES[form.best_of] || 15;

  // Taking a court off the session takes it off every division too — it used
  // to stay in the division and keep getting matches.
  const toggleCourt = (id) => {
    if (form.courts_used.includes(id)) {
      const divisions = form.divisions.map(d => ({ ...d, court_ids: (d.court_ids || []).filter(c => c !== id) }));
      const orphan = divisions.find(d => d.court_ids.length === 0);
      if (orphan) toast.warning(`${orphan.name} has no courts now — give it one in Divisions.`);
      setForm(f => ({ ...f, courts_used: f.courts_used.filter(c => c !== id), divisions }));
    } else {
      set('courts_used', [...form.courts_used, id]);
    }
  };
  const toggleDivCourt = (id) => setDivCourts(
    divCourts.includes(id) ? divCourts.filter(c => c !== id) : [...divCourts, id]
  );

  // Editing a division changes it IN PLACE (same id): removing and re-adding it
  // used to give it a new id and strand everyone already registered in it.
  const [editingDivId, setEditingDivId] = useState(null);
  // Level range on the 1-10 bar (optional; both ends or none, no overlaps —
  // owner 2026-09-27). '' = no range.
  const [divMin, setDivMin] = useState('');
  const [divMax, setDivMax] = useState('');
  const resetDivForm = () => { setDivName(''); setDivCap(''); setCapTouched(false); setDivCourts([]); setDivMin(''); setDivMax(''); };
  const addDivision = () => {
    const n = divName.trim();
    if (!n) { toast.error('Division name is required'); return; }
    if (!divCap || parseInt(divCap) < 4) { toast.error('Hard cap must be at least 4'); return; }
    if (divCourts.length === 0) { toast.error('Assign at least one court to this division'); return; }
    if ((divMin === '') !== (divMax === '')) { toast.error('Set both the lowest and highest level, or neither'); return; }
    const lo = divMin === '' ? null : parseInt(divMin);
    const hi = divMax === '' ? null : parseInt(divMax);
    if (lo != null && lo > hi) { toast.error('The lowest level is above the highest'); return; }
    const entry = { name: n, hard_cap: parseInt(divCap), court_ids: [...divCourts], min_level: lo, max_level: hi };
    const next = editingDivId
      ? form.divisions.map(d => (d.id === editingDivId ? { ...d, ...entry } : d))
      : [...form.divisions, { id: `div-${Date.now()}`, ...entry }];
    const clash = rangeOverlap(next);
    if (clash) { toast.error(clash); return; }
    set('divisions', next);
    setEditingDivId(null);
    resetDivForm();
  };
  const editDivision = (d) => {
    setEditingDivId(d.id); setDivName(d.name); setDivCap(String(d.hard_cap || '')); setCapTouched(true); setDivCourts([...(d.court_ids || [])]);
    setDivMin(d.min_level != null ? String(d.min_level) : ''); setDivMax(d.max_level != null ? String(d.max_level) : '');
  };
  const removeDivision = (id) => set('divisions', form.divisions.filter(d => d.id !== id));

  // Recommended players per court (owner 2026-09-27): 12–16 for doubles,
  // half that for singles. Above the top end, waits between games get long.
  const perCourt = form.game_mode === 'doubles' ? { min: 12, max: 16 } : { min: 6, max: 8 };
  const getRecommendation = (courtCount) => ({ min: perCourt.min * courtCount, max: perCourt.max * courtCount });

  // The max-players box fills itself with the top of the range for the courts
  // picked, until the owner types their own number.
  const [capTouched, setCapTouched] = useState(false);
  useEffect(() => {
    if (!capTouched && divCourts.length > 0) setDivCap(String(perCourt.max * divCourts.length));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divCourts.length, perCourt.max, capTouched]);

  const next = () => {
    if (step === 1 && !form.name.trim()) { toast.error('Session name is required'); return; }
    if (step === 1 && !form.date) { toast.error('Date is required'); return; }
    if (step === 3 && form.courts_used.length === 0) { toast.error('Select at least one court'); return; }
    if (step === 4 && form.divisions.length === 0) { toast.error('Add at least one division'); return; }
    if (step === 4 && form.divisions.some(d => !(d.court_ids || []).length)) {
      toast.error('Every division needs at least one court'); return;
    }
    if (step === 2 && !form.unlimited_scoring && !(parseInt(form.scoring_target, 10) >= 1)) {
      toast.error('Enter a scoring target (a whole number), or choose Unlimited'); return;
    }
    // Step 6 = Check-In & PIN. PIN is REQUIRED because it also unlocks scorer override.
    if (step === 6 && !/^\d{4,6}$/.test(form.scorer_pin || '')) {
      toast.error('Session PIN is required — 4 to 6 digits. This PIN also unlocks scorer override.');
      return;
    }
    setStep(s => Math.min(s + 1, STEPS.length));
  };
  const back = () => setStep(s => Math.max(s - 1, 1));

  const handleSubmit = async () => {
    setSaving(true);
    const payload = { ...form, scoring_target: form.unlimited_scoring ? null : (parseInt(form.scoring_target, 10) || null), win_by: form.win_by || 2 };
    delete payload.unlimited_scoring;
    try {
      if (editId) {
        await sportsApi.put(`/sports/open-play/${editId}`, payload);
        toast.success('Session updated');
      } else {
        const res = await sportsApi.post('/sports/open-play', payload);
        toast.success('Session saved as Draft — click "Open Registration" when you\'re ready to go live', { duration: 5000 });
        navigate(`/sports/open-play/${res.data.id}`);
        return;
      }
      navigate('/sports/tournaments');
    } catch (e) {
      if (e.response?.status === 402) {
        const msg = e.response?.data?.detail?.message || e.response?.data?.detail || '';
        toast.error(msg || 'Trial limit reached. Contact 09952568450 to upgrade.', { duration: 8000 });
      } else {
        const d = e.response?.data?.detail;
        toast.error((d && typeof d === 'object' ? d.message : d) || 'Failed to save', { duration: 7000 });
      }
    } finally {
      setSaving(false);
    }
  };

  const assignedCourts = form.divisions.flatMap(d => d.court_ids || []);
  const availableCourtsForDiv = form.courts_used.filter(c => !assignedCourts.includes(c) || divCourts.includes(c));

  return (
    <div className="min-h-screen" style={{ background: '#0A0A0A' }}>
      <SportsNavBar />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/sports/tournaments')} className="p-1 rounded-md"
            style={{ color: '#737373' }}>
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h1 className="text-xl font-bold tracking-tight uppercase"
            style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>
            {editId ? 'Edit Session' : 'New Open Play Session'}
          </h1>
        </div>

        {/* Stepper */}
        <div className="flex gap-1 mb-8">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1">
              <div className="h-1 rounded-full" style={{
                background: i + 1 <= step ? '#E63946' : '#262626',
                transition: 'background 0.3s',
              }} />
              <p className="text-xs mt-1 text-center hidden sm:block"
                style={{ color: i + 1 === step ? '#E63946' : '#555' }}>{s}</p>
            </div>
          ))}
        </div>

        {/* Step content */}
        <div className="rounded-xl p-6" style={{ background: '#141414', border: '1px solid #262626' }}>

          {/* STEP 1 — Basics */}
          {step === 1 && (
            <div className="space-y-5">
              <div>
                <label style={labelStyle}>Session Name *</label>
                <input data-testid="op-name" style={inputStyle} value={form.name}
                  onChange={e => set('name', e.target.value)} placeholder="e.g. Summer Open Play" />
              </div>
              <div>
                <label style={labelStyle}>Description</label>
                <textarea data-testid="op-desc" style={{ ...inputStyle, minHeight: 70 }} value={form.description}
                  onChange={e => set('description', e.target.value)} placeholder="Optional event description..." />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label style={labelStyle}>Date *</label>
                  <CalendarPicker testid="op-date" value={form.date} min={today}
                    onChange={v => set('date', v)} placeholder="Pick session date" />
                </div>
                <div>
                  <label style={labelStyle}>Start Time</label>
                  <select data-testid="op-start" style={inputStyle} value={form.time_start}
                    onChange={e => set('time_start', e.target.value)}>
                    {TIMES.map(t => <option key={t} value={t}>{displayTime(t)}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>End Time</label>
                  <select data-testid="op-end" style={inputStyle} value={form.time_end}
                    onChange={e => set('time_end', e.target.value)}>
                    {TIMES.map(t => <option key={t} value={t}>{displayTime(t)}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label style={labelStyle}>Contact Name</label>
                  <input style={inputStyle} value={form.contact_name}
                    onChange={e => set('contact_name', e.target.value)} placeholder="Coordinator name" />
                </div>
                <div>
                  <label style={labelStyle}>Contact Email</label>
                  <input style={inputStyle} type="email" value={form.contact_email}
                    onChange={e => set('contact_email', e.target.value)} />
                </div>
                <div>
                  <label style={labelStyle}>Contact Phone</label>
                  <input style={inputStyle} value={form.contact_phone}
                    onChange={e => set('contact_phone', e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {/* STEP 2 — Format */}
          {step === 2 && (
            <div className="space-y-5">
              <div>
                <label style={labelStyle}>Game Mode</label>
                <div className="grid grid-cols-2 gap-3">
                  <OptionCard active={form.game_mode === 'doubles'} title="Doubles"
                    desc="4 players per match, partners rotate" testid="op-doubles"
                    onClick={() => set('game_mode', 'doubles')} />
                  <OptionCard active={form.game_mode === 'singles'} title="Singles"
                    desc="2 players per match, head-to-head" testid="op-singles"
                    onClick={() => set('game_mode', 'singles')} />
                </div>
              </div>
              <div>
                <label style={labelStyle}>
                  Scoring Target (per game)
                  <Tooltip text="First to reach this score wins the game. Leave unlimited for manual End Match." />
                </label>
                <div className="grid grid-cols-2 gap-3 mb-2">
                  <OptionCard active={!form.unlimited_scoring} title="Target Score"
                    desc="Match auto-ends at score limit" testid="op-target-on"
                    onClick={() => set('unlimited_scoring', false)} />
                  <OptionCard active={form.unlimited_scoring} title="Unlimited"
                    desc="Manual end only" testid="op-target-off"
                    onClick={() => set('unlimited_scoring', true)} />
                </div>
                {!form.unlimited_scoring && (
                  <div className="flex gap-2 flex-wrap">
                    {[11, 15, 21, 25].map(v => (
                      <button key={v} data-testid={`op-target-${v}`}
                        onClick={() => set('scoring_target', v)}
                        className="px-3 py-1.5 rounded-lg text-sm font-bold"
                        style={{
                          background: Number(form.scoring_target) === v ? 'rgba(230,57,70,0.15)' : '#1A1A1A',
                          color: Number(form.scoring_target) === v ? '#E63946' : '#737373',
                          border: `1px solid ${Number(form.scoring_target) === v ? '#E63946' : '#2A2A2A'}`,
                        }}>
                        {v} pts
                      </button>
                    ))}
                    <input inputMode="numeric" value={form.scoring_target}
                      onChange={e => set('scoring_target', e.target.value.replace(/\D/g, '').slice(0, 3))}
                      style={{ ...inputStyle, maxWidth: 80, padding: '6px 10px' }}
                      data-testid="op-target-custom"
                      placeholder="Custom" />
                  </div>
                )}
                {!form.unlimited_scoring && (
                  <div className="mt-3">
                    <label style={{ ...labelStyle, marginTop: 0 }}>
                      Winning Margin (Deuce)
                      <Tooltip text="Win by 2 = must lead by 2 points at target (e.g. game continues past 11-11 until 13-11). Win by 1 = whoever hits target first wins. Only applies to auto-completion; the scorer can always force-end a match with PIN." />
                    </label>
                    <div className="flex gap-2 flex-wrap mt-1">
                      {[
                        { v: 1, title: 'Win by 1', desc: 'First to target wins' },
                        { v: 2, title: 'Win by 2', desc: 'Deuce — standard rule' },
                      ].map(opt => (
                        <button key={opt.v} data-testid={`op-winby-${opt.v}`}
                          onClick={() => set('win_by', opt.v)}
                          className="px-3 py-1.5 rounded-lg text-sm font-bold text-left"
                          style={{
                            background: form.win_by === opt.v ? 'rgba(230,57,70,0.15)' : '#1A1A1A',
                            color: form.win_by === opt.v ? '#E63946' : '#737373',
                            border: `1px solid ${form.win_by === opt.v ? '#E63946' : '#2A2A2A'}`,
                            minWidth: 160,
                          }}>
                          {opt.title}
                          <span className="block text-[10px] font-normal mt-0.5" style={{
                            color: form.win_by === opt.v ? 'rgba(230,57,70,0.8)' : '#525252'
                          }}>{opt.desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label style={labelStyle}>
                  How should players be matched?
                </label>
                <p className="text-xs mb-2" style={{ color: '#A3A3A3' }}>
                  Everyone&apos;s level (skill bar or DUPR) is used either way, and the app learns from real results during the night.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <OptionCard active={!form.skill_based_pairing} title="Social (default)"
                    desc={<>Close, fun games with lots of new partners and opponents.
                      <span className="block mt-1" style={{ color: '#555' }}>Best for regular open play.</span></>}
                    testid="op-skill-off"
                    onClick={() => set('skill_based_pairing', false)} />
                  <OptionCard active={form.skill_based_pairing} title="Competitive"
                    desc={<>You play the people closest to your level.
                      <span className="block mt-1" style={{ color: '#555' }}>Tighter games, fewer new faces.</span></>}
                    testid="op-skill-on"
                    onClick={() => set('skill_based_pairing', true)} />
                </div>
              </div>
              <div>
                <label style={labelStyle}>
                  Mixing in beginners
                </label>
                <p className="text-xs mb-2" style={{ color: '#A3A3A3' }}>
                  When all levels play together, the weakest players can end up only playing each other.
                  Choose how often they get mixed into games with stronger players.
                </p>
                {(() => {
                  const current = form.balancing_factor || (form.skill_based_pairing ? 'low' : 'medium');
                  // Owner 2026-09-27: say what players will feel. Figures from
                  // the pool lab (docs/OPEN-PLAY-FAIR-PAIRING-PLAN §15).
                  const feel = {
                    low: <>Strong players mostly face <b>similar levels</b>. Beginners play <b>each other</b> most of the time (about 1 game in 5 is beginners-only).</>,
                    medium: <>Beginners play each other <b>now and then</b> (about 1 game in 8) but <b>never twice in a row</b>. Strong players meet a beginner <b>sometimes</b>.</>,
                    high: <>Beginners are <b>always mixed in</b> — no beginners-only games. Strong players will <b>often</b> have a beginner on their court.</>,
                  }[current];
                  return (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {[['low', 'Low', 'Beginners mostly play beginners. Strong players stay with strong players.', 'Stronger players rarely meet a beginner.'],
                          ['medium', 'Medium', 'Beginners never get two beginners-only games in a row.', 'A fair middle.'],
                          ['high', 'High', 'Beginners are mixed into almost every game.', 'Stronger players share courts with beginners more often.']].map(([val, title, desc, note]) => (
                          <OptionCard key={val} active={current === val}
                            title={`${title}${!form.balancing_factor && current === val ? ' (default)' : ''}`}
                            desc={<>{desc}<span className="block mt-1" style={{ color: '#555' }}>{note}</span></>}
                            testid={`op-balance-${val}`}
                            onClick={() => set('balancing_factor', val)} />
                        ))}
                      </div>
                      <div data-testid="op-balance-feel" className="mt-3 p-3 rounded-xl text-xs"
                        style={{ background: '#111', border: '1px solid #2A2A2A', color: '#D4D4D4', lineHeight: 1.5 }}>
                        <b style={{ color: '#fff' }}>What players will feel:</b> {feel}
                      </div>
                    </>
                  );
                })()}
              </div>
              <div>
                <label style={labelStyle}>
                  Gender Balance (early rounds)
                  <Tooltip text="Before real results exist, self-rated skill under-weights the physical gap, so equal-rated men can crush equal-rated women. When ON, early games split the sexes across teams (1M+1F vs 1M+1F), then fade to pure skill after a few games. Only affects who is paired — never scores, records, or rankings. No effect unless players have a gender set." />
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <OptionCard active={form.gender_balance} title="ON — Balance early"
                    desc="Mix M/F on teams for the first few games, then let skill take over" testid="op-gender-on"
                    onClick={() => set('gender_balance', true)} />
                  <OptionCard active={!form.gender_balance} title="OFF"
                    desc="Ignore gender entirely — pure skill/variety from the start" testid="op-gender-off"
                    onClick={() => set('gender_balance', false)} />
                </div>
              </div>
            </div>
          )}

          {/* STEP 3 — Courts */}
          {step === 3 && (
            <div className="space-y-4">
              <label style={labelStyle}>Select Courts Active for This Session *</label>
              {courts.length === 0 ? (
                <p className="text-sm" style={{ color: '#737373' }}>No courts found. Add courts first in My Courts.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {courts.map(c => {
                    const active = form.courts_used.includes(c.id);
                    return (
                      <button key={c.id} type="button" data-testid={`op-court-${c.id}`}
                        onClick={() => toggleCourt(c.id)}
                        className="flex items-center gap-3 p-3 rounded-xl transition-all text-left"
                        style={{
                          background: active ? 'rgba(230,57,70,0.12)' : '#0A0A0A',
                          border: `1px solid ${active ? '#E63946' : '#2A2A2A'}`,
                        }}>
                        <SportIcon sport={c.sport} size={20} />
                        <div>
                          <p className="text-sm font-bold" style={{ color: '#fff' }}>{c.name}</p>
                          <p className="text-xs" style={{ color: '#737373' }}>{c.sport}</p>
                        </div>
                        {active && <Check className="h-4 w-4 ml-auto" style={{ color: '#E63946' }} />}
                      </button>
                    );
                  })}
                </div>
              )}
              {form.courts_used.length > 0 && (
                <p className="text-xs" style={{ color: '#A3A3A3' }}>
                  {form.courts_used.length} court{form.courts_used.length !== 1 ? 's' : ''} selected
                </p>
              )}
            </div>
          )}

          {/* STEP 4 — Divisions */}
          {step === 4 && (
            <div className="space-y-5">
              <div>
                <label style={labelStyle}>Divisions</label>
                <p className="text-xs mb-3" style={{ color: '#737373' }}>
                  Each division is an isolated rotation pool with its own courts and player cap.
                </p>
              </div>

              {/* Existing divisions */}
              {form.divisions.map(d => {
                const courtNames = (d.court_ids || []).map(cid => courts.find(c => c.id === cid)?.name || cid);
                return (
                  <div key={d.id} className="flex items-center justify-between p-3 rounded-lg"
                    style={{ background: '#0A0A0A', border: '1px solid #2A2A2A' }}>
                    <div>
                      <span className="text-sm font-bold" style={{ color: '#fff' }}>{d.name}</span>
                      <span className="text-xs ml-2" style={{ color: '#E63946' }}>max {d.hard_cap} players</span>
                      {hasRange(d) && (
                        <span data-testid={`op-div-range-${d.id}`} className="text-xs ml-2" style={{ color: '#22C55E' }}>
                          Level {d.min_level}–{d.max_level} ({fmtDuprRange(d.min_level, d.max_level)})
                        </span>
                      )}
                      <p className="text-xs mt-0.5" style={{ color: '#555' }}>Courts: {courtNames.join(', ')}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button data-testid={`op-edit-div-${d.id}`} onClick={() => editDivision(d)}
                        className="px-2 py-1 rounded text-xs font-bold" style={{ color: '#A3A3A3', border: '1px solid #2A2A2A' }}>
                        Edit
                      </button>
                      <button data-testid={`op-remove-div-${d.id}`} onClick={() => removeDivision(d.id)}
                        disabled={(d.player_count || 0) > 0}
                        title={(d.player_count || 0) > 0 ? `${d.player_count} registered — edit it instead of removing` : 'Remove division'}
                        className="p-1" style={{ color: '#737373', opacity: (d.player_count || 0) > 0 ? 0.3 : 1 }}>
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Add division form */}
              <div className="p-4 rounded-xl space-y-3" style={{ background: '#111', border: '1px dashed #333' }}>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label style={labelStyle}>Division Name</label>
                    <input data-testid="op-div-name" style={inputStyle} value={divName}
                      onChange={e => setDivName(e.target.value)} placeholder="e.g. Intermediate" />
                  </div>
                  <div>
                    <label style={labelStyle}>
                      Hard Cap (max players) *
                      {divCourts.length > 0 && (
                        <span data-testid="op-div-cap-rec" className="text-xs ml-1" style={{ color: '#22C55E', textTransform: 'none', letterSpacing: 0 }}>
                          Recommended: {perCourt.min}–{perCourt.max} per court
                          {divCourts.length > 1 ? ` → ${getRecommendation(divCourts.length).min}–${getRecommendation(divCourts.length).max}` : ''}
                        </span>
                      )}
                    </label>
                    <input data-testid="op-div-cap" type="number" style={inputStyle} value={divCap}
                      onChange={e => { setCapTouched(true); setDivCap(e.target.value); }}
                      placeholder={`e.g. ${perCourt.max * Math.max(1, divCourts.length)}`} min={4} />
                    {divCourts.length > 0 && parseInt(divCap) > getRecommendation(divCourts.length).max && (
                      <p data-testid="op-div-cap-warn" className="text-xs mt-1" style={{ color: '#F59E0B' }}>
                        More than {perCourt.max} per court means long waits between games — add a court or lower the cap.
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>
                    Skill Level Range (optional)
                    <Tooltip text="Only players whose level fits are recommended this division. Anyone outside sees a friendly suggestion for a better-fitting division — never blocked. Divisions with a range mix players freely inside it (the range already grouped them by skill). Ranges can't overlap." />
                  </label>
                  <div className="flex items-center gap-2 flex-wrap">
                    {[['from', divMin, setDivMin, 'op-div-min'], ['to', divMax, setDivMax, 'op-div-max']].map(([lbl, val, setter, tid]) => (
                      <label key={lbl} className="flex items-center gap-1.5 text-xs" style={{ color: '#A3A3A3' }}>
                        {lbl}
                        <select data-testid={tid} value={val} onChange={e => setter(e.target.value)}
                          style={{ ...inputStyle, width: 84, padding: '6px 8px' }}>
                          <option value="">—</option>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                            <option key={n} value={n}>{n} · DUPR {fmtDupr(n)}</option>
                          ))}
                        </select>
                      </label>
                    ))}
                    {divMin !== '' && divMax !== '' && (
                      <span data-testid="op-div-range-preview" className="text-xs" style={{ color: '#22C55E' }}>
                        Level {divMin}–{divMax} · {fmtDuprRange(Number(divMin), Number(divMax))}
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Assign Courts to This Division</label>
                  <div className="flex flex-wrap gap-2">
                    {availableCourtsForDiv.map(cid => {
                      const court = courts.find(c => c.id === cid);
                      const sel = divCourts.includes(cid);
                      return (
                        <button key={cid} type="button" onClick={() => toggleDivCourt(cid)}
                          data-testid={`op-div-court-${cid}`}
                          className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
                          style={{
                            background: sel ? 'rgba(230,57,70,0.15)' : '#0A0A0A',
                            border: `1px solid ${sel ? '#E63946' : '#333'}`,
                            color: sel ? '#E63946' : '#A3A3A3',
                          }}>
                          {court?.name || cid}
                        </button>
                      );
                    })}
                    {availableCourtsForDiv.length === 0 && (
                      <p className="text-xs" style={{ color: '#555' }}>All selected courts are assigned to other divisions</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid="op-add-div" onClick={addDivision}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold uppercase"
                    style={{ background: '#E63946', color: '#fff' }}>
                    {editingDivId ? 'Update Division' : <><Plus className="h-3 w-3" /> Add Division</>}
                  </button>
                  {editingDivId && (
                    <button data-testid="op-cancel-edit-div"
                      onClick={() => { setEditingDivId(null); resetDivForm(); }}
                      className="px-3 py-1.5 rounded-lg text-xs font-bold" style={{ color: '#A3A3A3', border: '1px solid #2A2A2A' }}>
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* STEP 5 — Registration */}
          {step === 5 && (
            <div className="space-y-5">
              <div>
                <label style={labelStyle}>Entry Fee</label>
                <div className="flex items-center gap-2">
                  <span style={{ color: '#737373', fontSize: 14 }}>₱</span>
                  <input data-testid="op-fee" type="number" style={{ ...inputStyle, maxWidth: 160 }}
                    value={form.entry_fee} onChange={e => set('entry_fee', e.target.value)}
                    placeholder="0 = Free" min={0} />
                </div>
                <p className="text-xs mt-1" style={{ color: '#555' }}>Leave empty or 0 for free sessions</p>
              </div>
              {parseFloat(form.entry_fee || 0) > 0 && (
                <div>
                  <label style={labelStyle}>Payment Options for Registrants</label>
                  <PayOptionsPicker value={form.payment_amount_options} testidPrefix="op-payopt"
                    onChange={v => set('payment_amount_options', v)} />
                  <p className="text-xs mt-1" style={{ color: '#555' }}>Choose how players can settle the entry fee. Online options need PayMongo or GCash configured in Settings.</p>
                </div>
              )}
              <div>
                <label style={labelStyle}>Registration Mode</label>
                <div className="grid grid-cols-3 gap-3">
                  <OptionCard active={form.registration_mode === 'open'} title="Open"
                    desc="Anyone can register" testid="op-reg-open"
                    onClick={() => set('registration_mode', 'open')} />
                  <OptionCard active={form.registration_mode === 'invite_only'} title="Invite Only"
                    desc="Only invited players" testid="op-reg-invite"
                    onClick={() => set('registration_mode', 'invite_only')} />
                  <OptionCard active={form.registration_mode === 'mixed'} title="Mixed"
                    desc="Open + invitations" testid="op-reg-mixed"
                    onClick={() => set('registration_mode', 'mixed')} />
                </div>
              </div>
              {form.registration_mode !== 'open' && (
                <div>
                  <label style={labelStyle}>Auto-Cancel Unresponded Invitations</label>
                  <select data-testid="op-rsvp-expire" style={{ ...inputStyle, cursor: 'pointer' }}
                    value={form.rsvp_expire_hours_before ?? ''}
                    onChange={e => set('rsvp_expire_hours_before', e.target.value === '' ? null : Number(e.target.value))}>
                    <option value="">Never auto-cancel</option>
                    <option value="6">6 hours before session</option>
                    <option value="12">12 hours before session</option>
                    <option value="24">1 day before session</option>
                    <option value="48">2 days before session</option>
                    <option value="72">3 days before session</option>
                  </select>
                  <p className="text-xs mt-1" style={{ color: '#737373' }}>Pending invitations still unanswered at this cutoff auto-expire, freeing the seat back to the public pool. Choose "Never" for full manual control.</p>
                </div>
              )}
            </div>
          )}

          {/* STEP 6 — Check-In Settings */}
          {step === 6 && (
            <div className="space-y-5">
              <div>
                <label style={labelStyle}>Check-In Mode</label>
                <div className="grid grid-cols-1 gap-3">
                  <OptionCard active={form.check_in_mode === 'both'} title="Both (Recommended)"
                    desc="Players can self check-in via QR, and coordinator can also check them in"
                    testid="op-checkin-both" onClick={() => set('check_in_mode', 'both')} />
                  <OptionCard active={form.check_in_mode === 'coordinator'} title="Coordinator Only"
                    desc="Only the coordinator can mark players as checked in"
                    testid="op-checkin-coord" onClick={() => set('check_in_mode', 'coordinator')} />
                </div>
              </div>
              <div>
                <label style={labelStyle}>
                  Session PIN <span style={{ color: '#E63946' }}>*</span>
                  <Tooltip text="4–6 digit PIN. REQUIRED. Gates starting/ending the session, generating slots, activating a court and completing a match — AND is the ONLY way to override a locked scorer device on this session." />
                </label>
                <input data-testid="op-scorer-pin" type="text" inputMode="numeric" maxLength={6}
                  value={form.scorer_pin || ''}
                  onChange={e => set('scorer_pin', e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="e.g. 1234"
                  style={{
                    ...inputStyle,
                    maxWidth: 200,
                    borderColor: /^\d{4,6}$/.test(form.scorer_pin || '') ? '#22C55E' : '#E63946',
                  }} />
                <p className="text-xs mt-1" style={{ color: '#F59E0B' }}>
                  Required. Same PIN unlocks scorer override — write it down before you close this page.
                </p>
              </div>
            </div>
          )}

          {/* STEP 7 — Review */}
          {step === 7 && (
            <div className="space-y-4">
              <h3 className="text-base font-bold uppercase" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>
                Review & {editId ? 'Update' : 'Create'}
              </h3>
              <div className="space-y-2 text-sm" style={{ color: '#A3A3A3' }}>
                <Row label="Name" value={form.name} />
                <Row label="Date" value={fmtDate(form.date)} />
                <Row label="Time" value={`${displayTime(form.time_start)} – ${displayTime(form.time_end)}`} />
                <Row label="Mode" value={`${form.game_mode} · one game per match`} />
                <Row label="Score Target" value={form.unlimited_scoring ? 'Unlimited (manual end)' : `First to ${form.scoring_target}${(form.win_by || 2) > 1 ? ` · Win by ${form.win_by || 2}` : ''}`} />
                <Row label="Match Duration" value={`~${estMatchMinutes} min — auto-tunes from live results`} />
                <Row label="Matching" value={form.skill_based_pairing ? 'Competitive' : 'Social'} />
                <Row label="Mixing in beginners" value={{ low: 'Low', medium: 'Medium', high: 'High' }[form.balancing_factor || (form.skill_based_pairing ? 'low' : 'medium')]} />
                <Row label="Gender Balance" value={form.gender_balance ? 'ON (early rounds, then fades)' : 'OFF'} />
                <Row label="Courts" value={form.courts_used.map(c => courts.find(ct => ct.id === c)?.name || c).join(', ')} />
                <Row label="Divisions" value={form.divisions.map(d => `${d.name} (${d.hard_cap} max, ${d.court_ids.length} courts${hasRange(d) ? `, level ${d.min_level}–${d.max_level}` : ''})`).join(' | ')} />
                <Row label="Entry Fee" value={form.entry_fee ? `₱${form.entry_fee}` : 'Free'} />
                <Row label="Registration" value={form.registration_mode} />
                <Row label="Check-In" value={form.check_in_mode.replace('_', ' ')} />
                <Row label="Session PIN" value={form.scorer_pin ? '🔒 ' + form.scorer_pin.replace(/./g, '•') : '⚠️ Missing'} />
              </div>

              {/* Capacity warning */}
              {form.divisions.some(d => d.hard_cap > getRecommendation(d.court_ids.length).max) && (
                <div data-testid="op-review-cap-warn" className="p-3 rounded-lg text-xs" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid #F59E0B', color: '#F59E0B' }}>
                  Some divisions allow more than {perCourt.max} players per court. Players will wait longer between games — add a court or lower the cap.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <button onClick={back} disabled={step === 1}
            className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm"
            style={{ color: step === 1 ? '#333' : '#A3A3A3', background: '#141414', border: '1px solid #262626' }}>
            <ChevronLeft className="h-4 w-4" /> Back
          </button>
          {step < STEPS.length ? (
            <button data-testid="op-next" onClick={next}
              className="flex items-center gap-1 px-5 py-2 rounded-lg text-sm font-bold uppercase"
              style={{ background: '#E63946', color: '#fff', fontFamily: 'Oswald, sans-serif' }}>
              Next <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button data-testid="op-submit" onClick={handleSubmit} disabled={saving}
              className="flex items-center gap-1 px-5 py-2 rounded-lg text-sm font-bold uppercase"
              style={{ background: '#E63946', color: '#fff', fontFamily: 'Oswald, sans-serif', opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving...' : (editId ? 'Update Session' : 'Create Session')}
              <Check className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between py-1.5" style={{ borderBottom: '1px solid #1C1C1C' }}>
      <span style={{ color: '#737373' }}>{label}</span>
      <span style={{ color: '#fff' }}>{value}</span>
    </div>
  );
}
